import { useEffect, useRef } from 'react';
import { usePageSeoContext } from '../../lib/PageSeoContext';
import { TRUST_BADGE_ENABLED_KEY, TRUST_BADGE_SCRIPT_KEY } from '../../lib/siteSettingsApi';

/** Renders a third-party trust-badge embed (e.g. Trustindex) pasted verbatim
 * by the admin. Admin-controlled from Settings > Settings, same pattern as
 * WhatsAppButton — only renders when both enabled and an embed snippet is
 * configured. Assigning the snippet via innerHTML parses any <script> tags
 * in it but never executes them, so they're re-created as real <script>
 * elements here — that's the only way the browser will actually run them. */
export default function TrustBadgeEmbed() {
  const { siteSettings } = usePageSeoContext();
  const enabled = siteSettings[TRUST_BADGE_ENABLED_KEY] !== '0';
  const script = siteSettings[TRUST_BADGE_SCRIPT_KEY];
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !script || !container) return;

    container.innerHTML = script;
    container.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of Array.from(oldScript.attributes)) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });

    return () => {
      container.innerHTML = '';
    };
  }, [enabled, script]);

  if (!enabled || !script) return null;
  return <div ref={containerRef} />;
}
